import React, { useEffect, useState, useRef, useMemo } from 'react';
import { getComprehensionIntro } from '../../services/interventionSelection';
import { COPY_STRINGS } from "../../constants/translatedCopy";
import { STATES, NO_SPEAK_TIMEOUT_MS, NO_SUBMIT_TIMEOUT_MS, NO_TEXT_SUBMIT_TIMEOUT_MS, MAX_WAIT_FOR_SCORED_RESPONSE_MS, NO_SUBMIT_TIMER_UPDATE_INTERVAL, DETAILED_CONSOLE_LOGGING, SHOW_LOADING_OVERLAY_TIMEOUT_MS, NO_TYPE_TIMEOUT_MS, POINTER_IDS, PAUSE_BETWEEN_FTUE_STEPS, } from './constants';
import useSpeechVolumeLevel from '../StudentPreChecks/Components/hooks/useSpeechVolumeLevel';
import { logSentryException } from "Services/util";
import { logSessionInfo } from '../../services/logger';
import { listen, abortListen } from 'Services/Listen';
import useCancellablePromise from "../../hooks/useCancellablePromise";
import { amiraGestures } from '../../components/Coach/GesturedSpeech';

const { interactions: STRINGS } = COPY_STRINGS;

const useInteractionController = ({ 
  onDone, 
  onFinishFTUE, 
  speech,
  interactions,
  storyId,
  phraseIndex,
  activityId,
  showFTUE,
  setDisplayPrompt,
  setShowPrompt,
  setShowStudentTurn,
  setShowTimer,
  setShowMicrohone,
  setShowSubmitButton,
  setShowRepeatButton,
  setShowHintButton,
  setShowExcerptsButton,
  setActiveFTUEPointer,
  setSubmitButtonEnabled,
  setRepeatButtonEnabled,
  setExcerptsButtonEnabled,
  setHintButtonEnabled,
  setShowPauseOverlay,
  setShowLoadingOverlay,
  setShowCheckmarkOverlay,
}) => {
  // State
  const { cancellableCPromise } = useCancellablePromise();
  const [volumeLevel, setVolumeLevel] = useState(0);
  // The prompt that will be said, set by either the interaction metadata OR the cis response
  const [currentPrompt, setCurrentPrompt] = useState("");
  const interactionsLinkedList = useMemo(() => {
    if (interactions) {
      return interactions.map((interaction, index) => ({ ...interaction, nextInteraction: interactions[index + 1] }));
    }
    return null;
  }, [interactions]);
  const [promptedForNoInput, setPromptedForNoInput] = useState(false);
  const [listenCompCallbackData, setListenCompCallbackData] = useState(null);
  const [useTextInput, setUseTextInput] = useState(false);
  const [textResponse, setTextResponse] = useState("");
  const [transcript, setTranscript] = useState(null);
  const [submitTimerElapsed, setSubmitTimerElapsed] = useState(0);
  const [submitTimerPaused, setSubmitTimerPaused] = useState(true);
  const [currentInteraction, setCurrentInteraction] = useState(null);
  // The "stage" of the dialogue.  Which level deep we are with the AI.
  const [currentDialogueLevel, setCurrentDialogueLevel] = useState(0);
  // Each time we start / stop listening for a stage, we need to have some indicator to store the audio segments separately
  const [currentTake, setCurrentTake] = useState(0);
  const [excerptsPage1, setExcerptsPage1] = useState("");
  const [excerptsPage2, setExcerptsPage2] = useState("");
  const [helpButtonsEnabled, setHelpButtonsEnabled] = useState(false);
  const [currentHint, setCurrentHint] = useState("");
  const [fakeActiveListeningStatus, setFakeActiveListeningStatus] = useState(false);
  
  // Refs
  const textResponseRef = useRef(textResponse);
  const inferenceTimedOutRef = useRef(false);
  const listenCompCallbackCPromiseRef = useRef(null);
  const transcriptRef = useRef(transcript);
  const inferenceStartTimeRef = useRef(null);
  const submitTimerRef = useRef(null);

  // Tutorial Management
  const [isInFTUE, setIsInFTUE] = useState(false);

  //#region State Machine
  const [currentState, setState] = useState(STATES.INITIALIZING);

  useEffect(() => {
    let onExit = () => { };

    detailedLog(`Comprehension Dialog: - ${Date.now().toString()} - Entering State - ${currentState}`);
    // Current State On Enter
    if (currentState === STATES.INITIALIZING) return;
    else if (currentState === STATES.TEST) onExit = testStateRun();
    else if (currentState === STATES.FTUE) onExit = ftueStateRun();
    else if (currentState === STATES.TUTOR_SPEAK_PROMPT) onExit = tutorSpeakPromptStateRun();
    else if (currentState === STATES.LISTEN_USER_INPUT) onExit = listenUserInputStateRun();
    else if (currentState === STATES.HANDLE_NO_INPUT) onExit = handleNoInputStateRun();
    else if (currentState === STATES.HANDLE_DONE_CLICKED) onExit = handleDoneClickedStateRun();
    else if (currentState === STATES.HANDLE_REPEAT_CLICKED) onExit = handleRepeatClickedStateRun();
    else if (currentState === STATES.HANDLE_EXCERPTS_CLICKED) onExit = handleExcerptsClickedStateRun();
    else if (currentState === STATES.HANDLE_HINT_CLICKED) onExit = handleHintClickedStateRun();
    else if (currentState === STATES.TUTOR_SPEAK_RESPONSE) onExit = tutorSpeakResponseStateRun();
    else if (currentState === STATES.CLEANUP) onExit = cleanupStateRun();

    // State On Exit
    //   Will be called when state changes
    return () => {
      detailedLog(`Interaction - ${Date.now().toString()} - Exiting State - ${currentState}`);

      onExit();
    }
  }, [currentState])
  //#endregion

  //#region UseEffects
  useEffect(() => {
    return () => {
      speech.stopListening();
      abortListen(
        speech,
        true // endOfPhrase
      )
    }
  }, []);

  // This effect updates the ref every time textResponse changes
  useEffect(() => {
    textResponseRef.current = textResponse;
  }, [textResponse]);

  const [volumeTimeout, setVolumeTimeout] = useState(null);
  useEffect(() => {
    transcriptRef.current = transcript;
    clearTimeout(volumeTimeout);
    setVolumeLevel(5);

    setVolumeTimeout(setTimeout(() => {
      setVolumeLevel(0);
    }, 1000))

  }, [transcript]);

  useEffect(() => {
    if (interactionsLinkedList) {
      let firstValidInteraction = interactionsLinkedList[0];
      while (!isInteractionValid(firstValidInteraction)) {
        if (!firstValidInteraction) {
          break;
        }
        firstValidInteraction = firstValidInteraction.nextInteraction;
      }
      if (firstValidInteraction) {
        speakOpening(firstValidInteraction);
      } else {
        setState(STATES.CLEANUP);
      }
    }
  }, [interactionsLinkedList]);

  useEffect(() => {
    if (currentInteraction) {
      // Setup dialogue
      setCurrentDialogueLevel(1);
      setCurrentPrompt(currentInteraction.prompt);
      setUseTextInput(currentInteraction.textInput);
      setShowStudentTurn(false);

      // On Enter / setup ui
      setTextResponse("");
      setTranscript(null);
      setPromptedForNoInput(false);
      setRepeatButtonEnabled(true);
      setExcerptsButtonEnabled(true);
      setHintButtonEnabled(true);
      setShowMicrohone(true);
      setShowTimer(true);
      setShowSubmitButton(true);

      parseExcerptsPages();

      // Change State, start the machine for this interaction.
      if (showFTUE) {
        setState(STATES.FTUE);
      } else {
        setState(STATES.TUTOR_SPEAK_PROMPT);
      }
    }
  }, [currentInteraction]);

  useEffect(() => {
    if (!submitTimerPaused) {
      submitTimerRef.current = setInterval(() => {
        setSubmitTimerElapsed(prevElapsed => prevElapsed + NO_SUBMIT_TIMER_UPDATE_INTERVAL);
      }, NO_SUBMIT_TIMER_UPDATE_INTERVAL);
    }

    return () => {
      if (submitTimerRef.current) {
        clearInterval(submitTimerRef.current);
      }
    };
  }, [submitTimerPaused]);

  useEffect(() => {
    if (!isInFTUE && submitTimerElapsed >= getNoSubmitTimeout()) {
      logSessionInfo('comprehension_no_submit_timeout_triggered', {});
      // STATE CHANGE
      setState(STATES.HANDLE_DONE_CLICKED);
    }
  }, [submitTimerElapsed]);

  useEffect(() => {
    if (currentPrompt && currentInteraction) {
      setCurrentHint(currentInteraction.hints?.find(hint => hint.prompt === currentPrompt)?.hint);
    } else {
      setCurrentHint(null);
    }
  }, [currentPrompt])
  //#endregion

  //#region Finite State Machine States
  function ftueStateRun() {
    function wait(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    }

    async function runFTUE() {
      logSessionInfo('comprehension_ftue_started', {});

      // Initialize the FTUE
      setIsInFTUE(true);

      // Disable everything
      setSubmitButtonEnabled(false);
      setHintButtonEnabled(false);
      setExcerptsButtonEnabled(false);

      // Hide everything
      setShowPrompt(false);
      setShowStudentTurn(false);
      setActiveFTUEPointer(null);

      // Mock Display Prompt
      setDisplayPrompt(STRINGS.FTUE.displayPrompt);

      // Coach: "I'm going to ask you a question about what you’ve read."
      // https://www.figma.com/design/tqzpAuAxrhpvbbIK5CQA43/Amira-%7C-Comprehension-2.0?node-id=208-3950&m=dev
      // https://www.figma.com/design/tqzpAuAxrhpvbbIK5CQA43/Amira-%7C-Comprehension-2.0?node-id=208-4114&m=dev
      await Promise.all([
        speak(STRINGS.FTUE.start).promise,
        new Promise(async resolve => {
          // These little awaits help control the flow.
          // The design calls for amira to often start talking before the actual 
          //   thing she's talking about appears during this tutorial.
          await wait(2000);
          setShowPrompt(true);  
          resolve();
        }),
      ]);

      // https://www.figma.com/design/tqzpAuAxrhpvbbIK5CQA43/Amira-%7C-Comprehension-2.0?node-id=348-3765&m=dev
      // This is the first example of just waiting between intstructions for pacing
      await wait(PAUSE_BETWEEN_FTUE_STEPS);

      if (useTextInput) {
        // Coach: "You can answer by typing into this box."
        setActiveFTUEPointer(POINTER_IDS.TEXT_BOX);
        await Promise.all([
          speak(STRINGS.FTUE.text).promise,
          new Promise(async resolve => {
            await wait(500);
            setShowStudentTurn(true);
            resolve();
          })
        ]);
      } else {
        // Coach: "You can answer by speaking into your microphone."
        // https://www.figma.com/design/tqzpAuAxrhpvbbIK5CQA43/Amira-%7C-Comprehension-2.0?node-id=348-3882&m=dev
        // https://www.figma.com/design/tqzpAuAxrhpvbbIK5CQA43/Amira-%7C-Comprehension-2.0?node-id=208-4224&m=dev
        await Promise.all([
          speak(STRINGS.FTUE.microhpone).promise,
          new Promise(async resolve => {
            await wait(2000);
            setShowStudentTurn(true);
            resolve();
          })
        ]);

        // Coach: "When the microphone icon looks like this, you'll know I'm listening."
        // https://www.figma.com/design/tqzpAuAxrhpvbbIK5CQA43/Amira-%7C-Comprehension-2.0?node-id=348-4118&m=dev
        setActiveFTUEPointer(POINTER_IDS.MICROPHONE);
        setFakeActiveListeningStatus(true);
        await speak(STRINGS.FTUE.microphone2).promise;
      }
      
      // https://www.figma.com/design/tqzpAuAxrhpvbbIK5CQA43/Amira-%7C-Comprehension-2.0?node-id=348-4002&m=dev
      await wait(PAUSE_BETWEEN_FTUE_STEPS);

      // https://www.figma.com/design/tqzpAuAxrhpvbbIK5CQA43/Amira-%7C-Comprehension-2.0?node-id=348-4364&m=dev
      setActiveFTUEPointer(null);
      setFakeActiveListeningStatus(false);
      await wait(PAUSE_BETWEEN_FTUE_STEPS);

      // Coach: "The timer will show you how much time you have left to answer."
      // https://www.figma.com/design/tqzpAuAxrhpvbbIK5CQA43/Amira-%7C-Comprehension-2.0?node-id=350-4448&m=dev
      setActiveFTUEPointer(POINTER_IDS.TIMER);
      setSubmitTimerElapsed(getNoSubmitTimeout());
      await speak(STRINGS.FTUE.timer).promise;

      // https://www.figma.com/design/tqzpAuAxrhpvbbIK5CQA43/Amira-%7C-Comprehension-2.0?node-id=350-4538&m=dev
      setActiveFTUEPointer(null);
      setSubmitTimerElapsed(0);
      await wait(PAUSE_BETWEEN_FTUE_STEPS);

      // Coach: "If you want to see the text again, use the passage button."
      // https://www.figma.com/design/tqzpAuAxrhpvbbIK5CQA43/Amira-%7C-Comprehension-2.0?node-id=208-4334&m=dev
      setRepeatButtonEnabled(true);
      setActiveFTUEPointer(POINTER_IDS.REPEAT_BUTTON);
      await speak(STRINGS.FTUE.repeatButton).promise;

      // https://www.figma.com/design/tqzpAuAxrhpvbbIK5CQA43/Amira-%7C-Comprehension-2.0?node-id=208-4448&m=dev
      setRepeatButtonEnabled(false);
      setActiveFTUEPointer(null);
      await wait(PAUSE_BETWEEN_FTUE_STEPS);

      // Coach: "If you want to see the text again, use the passage button."
      // https://www.figma.com/design/tqzpAuAxrhpvbbIK5CQA43/Amira-%7C-Comprehension-2.0?node-id=208-4538&m=dev
      setExcerptsButtonEnabled(true);
      setActiveFTUEPointer(POINTER_IDS.EXCERPTS_BUTTON)
      await speak(STRINGS.FTUE.excerptsButton).promise;

      // https://www.figma.com/design/tqzpAuAxrhpvbbIK5CQA43/Amira-%7C-Comprehension-2.0?node-id=208-4641&m=dev
      setExcerptsButtonEnabled(false);
      setActiveFTUEPointer(null);
      await wait(PAUSE_BETWEEN_FTUE_STEPS);

      // Coach: "If you’re not sure how to answer, use the hint button and I can help you!"
      // https://www.figma.com/design/tqzpAuAxrhpvbbIK5CQA43/Amira-%7C-Comprehension-2.0?node-id=208-4732&m=dev
      setHintButtonEnabled(true);
      setActiveFTUEPointer(POINTER_IDS.HINT_BUTTON);
      await speak(STRINGS.FTUE.hintButton).promise;

      // https://www.figma.com/design/tqzpAuAxrhpvbbIK5CQA43/Amira-%7C-Comprehension-2.0?node-id=208-4833&m=dev
      setHintButtonEnabled(false);
      setActiveFTUEPointer(null);
      await wait(PAUSE_BETWEEN_FTUE_STEPS);

      // Coach: "When you done answering, use the share button to hear what I think."
      // https://www.figma.com/design/tqzpAuAxrhpvbbIK5CQA43/Amira-%7C-Comprehension-2.0?node-id=208-5009&m=dev
      setSubmitButtonEnabled(true);
      setActiveFTUEPointer(POINTER_IDS.SUBMIT_BUTTOM);
      await speak(STRINGS.FTUE.submit).promise;

      // https://www.figma.com/design/tqzpAuAxrhpvbbIK5CQA43/Amira-%7C-Comprehension-2.0?node-id=208-5108&m=dev
      setSubmitButtonEnabled(false);
      setActiveFTUEPointer(null);
      await wait(PAUSE_BETWEEN_FTUE_STEPS);

      // https://www.figma.com/design/tqzpAuAxrhpvbbIK5CQA43/Amira-%7C-Comprehension-2.0?node-id=208-5286&m=dev
      setShowPrompt(false);
      setShowStudentTurn(false);

      // Coach: "Ok, here's my question..."
      // https://www.figma.com/design/tqzpAuAxrhpvbbIK5CQA43/Amira-%7C-Comprehension-2.0?node-id=208-5197&m=dev
      await speak(STRINGS.FTUE.ready, null).promise;

      // Close up, move on
      setSubmitButtonEnabled(true);
      setRepeatButtonEnabled(true);
      setHintButtonEnabled(true);
      setExcerptsButtonEnabled(true);
      
      setIsInFTUE(false);

      if (onFinishFTUE) {
        onFinishFTUE();
      }

      setState(STATES.TUTOR_SPEAK_PROMPT);
    }

    runFTUE();

    return () => {}
  }

  const tutorSpeakPromptStateRun = () => {
    setSubmitButtonEnabled(false);
    setDisplayPrompt(currentPrompt);
    setShowPauseOverlay(false);
    setShowPrompt(true);
    let stateHasChanged = false;
    let currentAsyncOperation = null;

    // On Enter
    try {
      currentAsyncOperation = speak(currentPrompt);
      currentAsyncOperation.promise.then(() => {
        // State Change
        setState(STATES.LISTEN_USER_INPUT);
      });
    } catch (error) {
      if (!error.isCancelled && !stateHasChanged) throw (error);
    }

    // On Exit
    return () => {
      setShowPauseOverlay(true);
      setSubmitButtonEnabled(true);
      stateHasChanged = true;
      if (currentAsyncOperation) {
        currentAsyncOperation.cancel();
      }
    };
  }

  const listenUserInputStateRun = () => {
    // On Enter
    setCurrentTake(currentTake + 1)
    setDisplayPrompt(currentPrompt);
    setShowStudentTurn(true);
    let stateChanged = false;
    let noInputTimeout;
    let helpButtonsTimeout;

    // BREAK THE FLOW
    // return () => {};

    try {
      setListenCompCallbackData(null);
      listenCompCallbackCPromiseRef.current = null;
      setSubmitButtonEnabled(true);
      setSubmitTimerElapsed(0);
      setSubmitTimerPaused(false);

      // Set no speak timeout
      if (!promptedForNoInput) {
        noInputTimeout = setTimeout(() => {
          if (currentDialogueLevel === 1 && !userInputExists() && !promptedForNoInput) {
            logSessionInfo("comprehension_no_input_timeout", {});
            // State Change
            setState(STATES.HANDLE_NO_INPUT);
          }
        }, useTextInput ? NO_TYPE_TIMEOUT_MS : NO_SPEAK_TIMEOUT_MS);
      }

      if (!helpButtonsEnabled) {
        helpButtonsTimeout = setTimeout(() => {
          setHelpButtonsEnabled(true);
        })
      }

      if (!useTextInput) {
        // Small delay, mic is enabled too quickly and capturing the very tail end of previous patter
        setTimeout(() => {
          setShowPauseOverlay(false);
          startListening();
        }, 250);
      } else {
        setShowPauseOverlay(false);
      }
    } catch (error) {
      if (!error.isCancelled && !stateChanged) throw (error);
    }

    // On Exit
    return () => {
      stateChanged = true;
      setShowPauseOverlay(true);
      clearTimeout(noInputTimeout);
      clearTimeout(helpButtonsTimeout);
      setSubmitButtonEnabled(false);
      setSubmitTimerPaused(true);
    };
  }

  const handleNoInputStateRun = () => {
    triggerComprehensionInference();
    
    let stateChanged = false;
    let currentAsyncOperation = null;

    // On Enter
    try {
      setPromptedForNoInput(true);
      let noInputPrompt = null;
      if (currentInteraction.noResponsePrompt && currentInteraction.noResponsePrompt !== '') {
        noInputPrompt = currentInteraction.noResponsePrompt;
      } else if (useTextInput) {
        noInputPrompt = STRINGS.NO_INPUT_TEXT_PROMPT;
      } else {
        noInputPrompt = STRINGS.NO_INPUT_VOICE_PROMPT;
      }

      currentAsyncOperation = noInputPrompt ? speak(noInputPrompt) : emptyCPromise();

      currentAsyncOperation.promise
        .then(() => {
          // State Change
          setState(STATES.LISTEN_USER_INPUT);
        })
    } catch (error) {
      if (!error.isCancelled && !stateChanged) throw (error);
    }

    // On Exit
    return () => {
      stateChanged = true;
      if (currentAsyncOperation) {
        currentAsyncOperation.cancel();
      }
    };
  }

  const handleDoneClickedStateRun = () => {
    let stateChanged = false;
    let currentAsyncOperation = null;
    let onStallFinish = () => {};

    // On Enter
    try {

      // Only prompt the user if they are in first dialogue level.  Otherwise, they have goten through the first succesfully OR they've already gotten the prompt
      if (currentDialogueLevel === 1 && !userInputExists() && !promptedForNoInput) {
        // State Change
        setState(STATES.HANDLE_NO_INPUT);
      } else {
        triggerComprehensionInference(true);

        let loadingTimeout = setTimeout(() => {
          onStallFinish = stallForTime();
        }, SHOW_LOADING_OVERLAY_TIMEOUT_MS);

        let inferenceTimeout = setTimeout(() => {
          logSessionInfo('comprehension_inference_timeout', {});
          console.log('Comprehension Dialog: comprehension_inference_timeout occurred.  Moving on to response, going to be default response.');
          clearTimeout(inferenceTimeout);
          setListenCompCallbackData(null);
          inferenceTimedOutRef.current = true;
          setState(STATES.TUTOR_SPEAK_RESPONSE)
          if (currentAsyncOperation) {
            currentAsyncOperation.cancel();
          }
        }, MAX_WAIT_FOR_SCORED_RESPONSE_MS);

        currentAsyncOperation = listenCompCallbackCPromiseRef.current;
        currentAsyncOperation.promise.then(data => {
          clearTimeout(inferenceTimeout);
          clearTimeout(loadingTimeout);
          setListenCompCallbackData(data);
        }).catch(e => {
          console.error("Comprehension Dialog: ERROR in comp callback", e);
          clearTimeout(loadingTimeout);
          clearTimeout(inferenceTimeout);
          setListenCompCallbackData(null);
        }).finally(() => {
          // State Change
          setState(STATES.TUTOR_SPEAK_RESPONSE)
        });
      }
    } catch (error) {
      if (!error.isCancelled && !stateChanged) throw (error);
    }

    // On Exit
    return () => {
      stateChanged = true;
      if (onStallFinish) {
        onStallFinish();
      }
      if (currentAsyncOperation) {
        currentAsyncOperation.cancel();
      }
    };
  }

  const handleRepeatClickedStateRun = () => {
    triggerComprehensionInference();
    logSessionInfo("comprehension_repeat_button_clicked", {});
    setSubmitTimerElapsed(0);
    setRepeatButtonEnabled(false);
    // TODO: confirm this is intended behaviour
    setPromptedForNoInput(true);

    let stateHasChanged = false;
    let currentAsyncOperation = null;

    // On Enter
    try {
      currentAsyncOperation = speak(currentPrompt);
      currentAsyncOperation.promise.then(() => {
        // State Change
        setState(STATES.LISTEN_USER_INPUT);
      });
    } catch (error) {
      if (!error.isCancelled && !stateHasChanged) throw (error);
    }

    // On Exit
    return () => {
      stateHasChanged = true;
      if (currentAsyncOperation) {
        currentAsyncOperation.cancel();
      }
    };
  }

  const handleExcerptsClickedStateRun = () => {
    triggerComprehensionInference();
    logSessionInfo("comprehension_excerpts_button_clicked", {});
    setExcerptsButtonEnabled(false);
    setSubmitTimerElapsed(0);

    return () => { };
  }

  const handleHintClickedStateRun = () => {
    triggerComprehensionInference();
    
    let stateChanged = false;

    if (currentHint) {
      setHintButtonEnabled(false);
      setPromptedForNoInput(true);
      logSessionInfo("comprehension_hint_button_clicked", {});

      try {
        speak(currentHint).promise.then(() => setState(STATES.LISTEN_USER_INPUT));
      } catch (error) {
        if (!error.isCancelled && !stateChanged) throw (error);
      }
    } else {
      // Extra failsafe layer
      setHintButtonEnabled(false);
    }

    // On Exit
    return () => {
      stateChanged = true;
     };
  }

  const tutorSpeakResponseStateRun = () => {
    let stateChanged = false;
    let feedbackPromise = null;

    // On Enter
    try {
      let feedback = null;
      let prompt = null;
      let moveOn = false;

      function getGenericResponse() {
        return currentInteraction.response ? currentInteraction.response : STRINGS.DEFAULT_RESPONSES[Math.floor(Math.random() * STRINGS.DEFAULT_RESPONSES.length)];
      }

      if (!listenCompCallbackData) {
        moveOn = true;
        if (userInputExists(transcript, textResponse)) {
          feedback = getGenericResponse();
        } else {
          feedback = STRINGS.NO_INPUT_FINAL_SUBMISSION_RESPONSE;
        }
      } else {
        const { patter: feedbackPart, prompt: questionPart } = listenCompCallbackData;
        
        if (!questionPart || questionPart.trim().length === 0) {
          moveOn = true;
        } else {
          setCurrentPrompt(questionPart.trim());
        }

        if (listenCompCallbackData.endDialog) {
          moveOn = true;
        }

        if (!feedbackPart || feedbackPart.trim().length === 0) {
          moveOn = true;
          feedback = getGenericResponse();
        } else {
          feedback = feedbackPart.trim();
        }
      }

      if (moveOn) {
        setShowCheckmarkOverlay(true);
      }

      if (feedback) {
        feedbackPromise = speak(feedback);
      } else {
        feedbackPromise = emptyCPromise();
      }

      feedbackPromise.promise
        .then(() => {
          // Handle the next level of dialogue
          if (!moveOn) {
            setCurrentDialogueLevel(currentDialogueLevel + 1);
            setCurrentTake(0);
            setRepeatButtonEnabled(true);
            setHintButtonEnabled(true);
            setExcerptsButtonEnabled(true);
            setSubmitTimerElapsed(0);
            setTextResponse("");

            setState(STATES.LISTEN_USER_INPUT);
          }
          // If we are done with levels of dialogue, check for a next interaction OR close out 
          else {
            let nextValidInteraction = currentInteraction.nextInteraction;
            while (!isInteractionValid(nextValidInteraction)) {
              if (!nextValidInteraction) {
                break;
              }
              nextValidInteraction = nextValidInteraction.nextInteraction;
            }
            if (nextValidInteraction) {
              // State Change (Restart State Machine in currentInteraction useEffect)
              setCurrentInteraction(nextValidInteraction);
            } else {
              // State Change
              setState(STATES.CLEANUP);
            }
          }
        });
    } catch (error) {
      if (!error.isCancelled && !stateChanged) throw (error);
    }

    // On Exit
    return () => {
      setShowCheckmarkOverlay(false);
      stateChanged = true;
      if (feedbackPromise) {
        feedbackPromise.cancel();
      }
    };
  }

  const cleanupStateRun = () => {
    // On Enter (We call on done and we are done.)
    onDone();

    // On Exit
    return () => { };
  }
  //#endregion

  //#region Utility Methods
  const speakOpening = (firstInteraction) => {
    speech.speakWithPromise({ textToSpeak: getComprehensionIntro(), useGenerative: true })
      .finally(() => setCurrentInteraction(firstInteraction));
  }

  const startInferenceTimeTracker = () => {
    const now = Date.now();
    inferenceStartTimeRef.current = now;
  };

  const endInferenceTimeTracker = () => {
    const now = Date.now();
    const timeElapsed = now - inferenceStartTimeRef.current;
    const timeElapsedSeconds = timeElapsed / 1000;
    const payload = { 
      timestamp: now,
      timeEllapsed: `${timeElapsedSeconds.toFixed(3)} seconds`,
      timeEllapsedMs: timeElapsed,
    };
    logSessionInfo("comprehension_inference_received_by_component", payload);
    console.log("Comprehension Dialog: comprehension_inference_received_by_component", payload);
    inferenceStartTimeRef.current = null;
  };

  const triggerComprehensionInference = (comprehensionDone) => {
    startInferenceTimeTracker();

    if (useTextInput) {
      if (comprehensionDone) {
        const promise = new Promise((resolve) => {
          const payload = {
            questionId: currentInteraction.ids[0],
            prompt: currentPrompt,
            stage: currentDialogueLevel,
            studentResponse: textResponseRef.current && textResponseRef.current.length > 0 ? textResponseRef.current : ".",
            activityId,
            phraseIndex,
          };
          logSessionInfo("comprehension_text_inference_requested", payload);
          console.log("Comprehension Dialog: Sending text response to inference: ", payload);
          speech.saveCompTextResponse(payload, (data) => resolve(onComprehensionInference(data)));
        });

        listenCompCallbackCPromiseRef.current = cancellableCPromise(promise);
      }
    } else {
      const payload =  {
        stage: currentDialogueLevel,
        prompt: currentPrompt,
        questionId: currentInteraction.ids[0],
        storyId,
        phraseIndex,
      };
      if (comprehensionDone) {
        payload.comprehensionDone = true;
      } else {
        payload.comprehensionDiscard = true;
      }
      logSessionInfo("comprehension_audio_inference_requested", payload);
      console.log("Comprehension Dialog: Sending audio response to inference (comprehensionDone: true): ", payload);
      speech.disableRecording(comprehensionDone, payload);

      detailedLog(`Comprehension Dialog: ${Date.now().toString()} - Stopping listening (signal is sent) - ${currentState}`);
      speech.stopListening();
    }
  }

  function parseExcerptsPages() {
    const excerpts = currentInteraction.excerpts;

    // Calculate the total number of characters
    const totalLength = excerpts.reduce((sum, str) => sum + str.length, 0);
    
    // Target length is half of the total length
    const target = totalLength / 2;
  
    let runningTotal = 0;
    let splitIndex = 0;
    let minDifference = Infinity;
  
    for (let i = 0; i < excerpts.length; i++) {
      runningTotal += excerpts[i].length;
      const currentDifference = Math.abs(target - runningTotal);
  
      if (currentDifference < minDifference) {
        minDifference = currentDifference;
        splitIndex = i;
      }
    }
  
    // Concatenate the first part for X and the second part for Y
    const X = excerpts.slice(0, splitIndex + 1)
    const Y = excerpts.slice(splitIndex + 1)

    setExcerptsPage1(X);
    setExcerptsPage2 (Y);
  }

  const getNoSubmitTimeout = () => useTextInput ? NO_TEXT_SUBMIT_TIMEOUT_MS : NO_SUBMIT_TIMEOUT_MS;

  const speak = (textToSpeak) => {
    return cancellableCPromise(speech.speakWithPromise({ textToSpeak, useGenerative: true }));
  }

  const emptyCPromise = () => {
    return cancellableCPromise(Promise.resolve());
  }

  const listenCallback = (args, newTranscript) => {
    // Don't set transcript back to null if it is not null and newTranscript is null
    if ((transcript && newTranscript) || (!transcript && newTranscript)) {
      setTranscript(newTranscript);
    }
  }

  const isInteractionValid = (interaction) => {
    const valid = interaction && interaction.prompt && interaction.prompt !== '';
    if (!valid) {
      logSentryException(new Error('Story interaction incorrectly configured.'));
    }
    return valid;
  }

  const handleTextResponseChange = (event) => {
    setTextResponse(event.target.value);
  }

  const userInputExists = () => {
    if (!useTextInput && transcriptRef.current && transcriptRef.current.length) {
      const rawTranscript = transcriptRef.current;

      if (rawTranscript.trim().length) {
        return true;
      }
    }

    if (useTextInput && textResponseRef.current && textResponseRef.current.length) {
      const rawResponse = textResponseRef.current;

      if (rawResponse.trim().length) {
        return true;
      }
    }

    return false;
  }

  const startListening = () => {
    const promise = new Promise((resolve) => {
      listen({
        callbackOverride: listenCallback,
        speech,
        audioDestination: `comprehension/${currentInteraction.ids[0]}/${currentDialogueLevel}/${currentTake}`,
        useComprehension: true,
        storyId,
        phraseIndex: phraseIndex,
        activityId,
        callback: (data) => resolve(onComprehensionInference(data)),
      });
    });

    listenCompCallbackCPromiseRef.current = cancellableCPromise(promise);
  }

  const detailedLog = (message, data) => {
    if (DETAILED_CONSOLE_LOGGING) {
      console.log(message, data);
    }
  }

  const onComprehensionInference = (data) => {
    endInferenceTimeTracker();
    detailedLog("Comprehension Dialog: IN INFERENCE CALLBACK CALLBACK. COMP RESPONSE:", data)
    if (!data.error) {
      setListenCompCallbackData(data);
      return data;
    } else {
      setListenCompCallbackData(null);
      return null;
    }
  }

  const onClickSubmit = () => {
    if (isInFTUE) return;
    logSessionInfo("comprehension_submit_button_clicked", {});
    setState(STATES.HANDLE_DONE_CLICKED);
  };

  const onClickRepeat = () => !isInFTUE && setState(STATES.HANDLE_REPEAT_CLICKED);

  const onClickExcerpts = () => !isInFTUE && setState(STATES.HANDLE_EXCERPTS_CLICKED);
  
  const onClickHint = () => !isInFTUE && setState(STATES.HANDLE_HINT_CLICKED);

  const onCloseExcerpts = () => setState(STATES.LISTEN_USER_INPUT);

  const stallForTime = () => {
    // Trigger amira lil pose
    speech.playDance({gesture: amiraGestures.Thinking}, () => {

    });
    speak(STRINGS.TIME_STALLS[Math.floor(Math.random() * STRINGS.TIME_STALLS.length)]);

    // Undo any overlays or whatever here
    // return () => {
    //   speech.playDance({gesture: amiraGestures.CountEnd});
    // };
  }

  //#endregion

  return {
    handleTextResponseChange,
    onClickSubmit,
    useTextInput,
    textResponse,
    submitTimerElapsed,
    volumeLevel,
    excerptsPage1,
    excerptsPage2,
    currentInteraction,
    getNoSubmitTimeout,
    onCloseExcerpts,
    onClickRepeat,
    onClickExcerpts,
    onClickHint,
    currentState,
    helpButtonsEnabled,
    isInFTUE,
    currentHint,
    fakeActiveListeningStatus,
  }
}

export default useInteractionController;